import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { storePortalContext } from '@/lib/regionConfig';
import LoginForm from '@/components/auth/LoginForm';
import SignupForm from '@/components/auth/SignupForm';

const PATHS = { login: '/login', signup: '/signup' };

const OVERLAY_COPY = {
  login: {
    title: 'New here?',
    body: 'Create an account to convert, tag and file your accounts with Companies House and HMRC.',
    cta: 'Sign up',
  },
  signup: {
    title: 'Already registered?',
    body: 'Sign in to see your jobs, invoices and filings.',
    cta: 'Sign in',
  },
};

// Sign-in / sign-up page: the two forms sit side by side and a brand panel slides over whichever
// one is not in use. Below the md breakpoint there is no sliding, only the active form is shown.
const AuthSlider = ({ initialMode = 'login' }) => {
  const [searchParams] = useSearchParams();
  storePortalContext(searchParams);

  const [mode, setMode] = useState(initialMode);
  const isSignup = mode === 'signup';

  const switchTo = (next) => {
    setMode(next);
    // Keep the URL in step (bookmarks, refresh) without remounting the page and losing the slide.
    window.history.replaceState(window.history.state, '', `${PATHS[next]}${window.location.search}`);
  };

  const copy = OVERLAY_COPY[mode];
  const slide = 'transition-all duration-500 ease-in-out motion-reduce:transition-none';
  const panel = 'md:absolute md:inset-y-0 md:left-0 md:w-1/2 md:overflow-y-auto flex-col justify-center p-6 md:p-12';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 md:p-8">
      <div
        className="relative w-full max-w-4xl bg-card border border-border md:min-h-[780px] overflow-hidden"
        data-testid="auth-slider"
      >
        <div
          className={`${panel} ${slide} ${isSignup
            ? 'hidden md:flex md:translate-x-full md:opacity-0 md:pointer-events-none'
            : 'flex md:translate-x-0 md:opacity-100 md:z-10'}`}
          aria-hidden={isSignup}
          inert={isSignup}
        >
          <LoginForm active={!isSignup} onSwitch={() => switchTo('signup')} />
        </div>

        <div
          className={`${panel} ${slide} ${isSignup
            ? 'flex md:translate-x-full md:opacity-100 md:z-10'
            : 'hidden md:flex md:translate-x-0 md:opacity-0 md:pointer-events-none'}`}
          aria-hidden={!isSignup}
          inert={!isSignup}
        >
          <SignupForm active={isSignup} onSwitch={() => switchTo('login')} />
        </div>

        <div
          className={`hidden md:flex absolute top-0 left-1/2 h-full w-1/2 z-20 flex-col justify-between bg-primary text-primary-foreground p-12 ${slide} ${isSignup ? '-translate-x-full' : 'translate-x-0'}`}
          data-testid="auth-overlay"
        >
          <img src="/logo-white.png" alt="Digital Reporting - comply confidently" className="h-12 w-auto self-start" />
          <div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {copy.title}
            </h2>
            <p className="text-lg opacity-80 mb-8">{copy.body}</p>
            <button
              type="button"
              onClick={() => switchTo(isSignup ? 'login' : 'signup')}
              className="border border-primary-foreground px-8 py-2.5 text-sm font-medium hover:bg-primary-foreground hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground"
              data-testid="auth-overlay-switch"
            >
              {copy.cta}
            </button>
          </div>
          <p className="text-sm opacity-60">
            &copy; {new Date().getFullYear()} The Digital Reports Company Limited
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthSlider;
